import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";

import { onRestaurantSelected } from "igfd-commons/src/monitoring";

import { RestaurantTermsAcceptanceModal } from "@dashboard/components/RestaurantModal/RestaurantTermsAcceptanceModal";
import { WorkspaceSwitcherProvider } from "@dashboard/components/admin/shell/WorkspaceSwitcher/WorkspaceSwitcherProvider";
import { useSyncThemes } from "@dashboard/hooks/useSyncThemes";
import type { ReduxState } from "@dashboard/types";

import { AxiosRestaurantAuthZProvider } from "../context";
import { setMixpanelWorkspace } from "../mixpanel/mixpanel";

/**
 * Holds the listeners whose data is used by more than one page, so they are
 * kept in sync for as long as a restaurant is selected. Rendered below the
 * guard in the layout so its hooks can assume there is one.
 *
 * Themes are here because the sidebar logo comes from the default theme.
 */
const RestaurantScopedListeners = () => {
  useSyncThemes();
  return null;
};

/**
 * Once the user is authenticated, this layout provides the current Restaurant
 * and contextual information about the restaurant to all pages. This includes
 * currency, price formattting, country, name, restaurant uuid, etc.
 *
 * This layout expects the initial restaurant list is already loaded.
 *
 * Selection of the correct restaurant is managed by a modal in the header.
 */
export const RestaurantManagementLayout = () => {
  const selectedRestaurant = useSelector(
    (state: ReduxState) => state.restaurant.selectedRestaurant
  );
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );

  useEffect(() => {
    if (!selectedRestaurant) {
      return;
    }
    // Allow analytics to caputure the first restaurant that
    // is being loaded.
    onRestaurantSelected(selectedRestaurant);
  }, [selectedRestaurant?.uuid]);

  const accessibleRestaurants = useSelector(
    (state: ReduxState) => state.restaurant.accessibleRestaurants
  );
  const paymentPlan = useSelector((state: ReduxState) =>
    selectedRestaurant
      ? state.restaurant.paymentPlansByRestaurantUuid[selectedRestaurant.uuid]
      : undefined
  );
  useEffect(() => {
    if (!selectedRestaurant) return;
    setMixpanelWorkspace(
      selectedRestaurant,
      accessibleRestaurants.map((restaurant) => restaurant.uuid),
      paymentPlan
    );
  }, [selectedRestaurant, accessibleRestaurants, paymentPlan]);

  if (!selectedRestaurant) {
    // Admins usually have no restaurants of their own; they pick one from the
    // workspace list instead of creating one.
    return (
      <Navigate
        to={userIsAdmin ? "/admin/workspaces" : "/restaurants/create"}
        replace
      />
    );
  }

  return (
    // This adds restaurant uuid to axios calls. Keyed so the authZ provider
    // state is reset when the restaurant changes.
    <AxiosRestaurantAuthZProvider key={selectedRestaurant.uuid}>
      {/* App-wide admin workspace switcher (Cmd/Ctrl+K). No-op for
          non-admins. */}
      <WorkspaceSwitcherProvider>
        <RestaurantScopedListeners />
        <Outlet key={selectedRestaurant.uuid} />
        <RestaurantTermsAcceptanceModal />
      </WorkspaceSwitcherProvider>
    </AxiosRestaurantAuthZProvider>
  );
};
